<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        NotificationModal,
        PageLoading,
        SearchInput,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { isMobile } from "../../stores";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getText,
        replaceURL,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import { deleteIcon, editIcon, eyeIcon } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { _getDisplayDate } from "../PatientEncounter/helpers/encounterHelper.svelte";
    import AddEditCompanyDocument from "./AddEditCompanyDocument.svelte";

    export let params;

    let isGettingAgreementDocuments = false,
        agreementDocuments = [];

    let createEditAgreementDocumentModalOpen = false;
    let isViewAgreementDocumentModal = false;

    let deleteAgreementDocumentModalOpen = false,
        selectedAgreementDocument = {},
        isDeletingAgreementDocument = false;

    const documentTypeOptions = [
        { value: "0", label: "Terms and Conditions" },
        { value: "1", label: "Privacy Policy" },
    ];

    let filters = {};

    let page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "id",
            title: "Id",
            headerClass: "text-left px-3 ml-3 cursor-default rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getText(v.id),
        },
        {
            key: "documentType",
            title: "Type",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) =>
                getAgreementDocumentTypeLabel(
                    v.documentType ?? v.type,
                ),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getText(v.name),
        },
        {
            key: "versionNumber",
            title: "Version",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getText(v.versionNumber),
        },
        {
            key: "active",
            title: "Active",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => (v.active ? "Yes" : "No"),
        },
        {
            key: "updatedDate",
            title: "Last update",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => _getDisplayDate(v.updatedDate),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(v),
            csvExportHidden: true,
        },
    ];


    onMount(async () => {
        const documentIdFromURL = _getDocumentIdFromURL();

        if (params.query) _updateSearchParamsByQuery(params.query);

        await loadAgreementDocuments(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        if (documentIdFromURL)
            await _openAgreementDocumentById(documentIdFromURL);
    });

    const loadAgreementDocuments = async (start, limit, requestParams) => {
        isGettingAgreementDocuments = true;

        await apiMethods.getMethod
            .getAgreementDocuments(start, limit, requestParams)
            .then((res) => _handleAgreementDocuments(res))
            .catch((err) => showException(err));

        isGettingAgreementDocuments = false;
    };

    const reload = async () => {
        await loadAgreementDocuments(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );
    }

    const deleteAgreementDocument = async () => {
        isDeletingAgreementDocument = true;

        await apiMethods.deleteMethod
            .deleteAgreementDocument(selectedAgreementDocument.id)
            .then((res) => {
                if (!res || !res.id)
                    showError(
                        "Failed to delete agreement document, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadAgreementDocuments(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
            })
            .catch((err) => showException(err));

        _closeDeleteAgreementDocumentModal();

        isDeletingAgreementDocument = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadAgreementDocuments(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/CompanyDocuments/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleClickCell = (event, rowData, key) => {
        const { className } = event.target;

        if (key == "buttons") {
            if (className.includes("deleteIcon"))
                _openDeleteAgreementDocumentModal(rowData);
            else if (className.includes("editIcon"))
                _openCreateEditAgreementDocumentModal(rowData);
            else if (className.includes("viewIcon"))
                _openCreateEditAgreementDocumentModal(rowData, { viewMode: true });
        }
    };

    const handleClickCol = () => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAgreementDocuments(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAgreementDocuments(0, pageSize, _getSearchParams());
    };

    const _handleAgreementDocuments = async (res) => {
        if (!res)
            showError(
                "Failed to load agreement documents, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.error) showError(res.error);
        else {
            agreementDocuments = [...res.results];
            count = res.total;
        }
    };

    const _updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();
            }
 
            updateURL();
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = (row) => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="viewIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="View Agreement Document">
                        <i class="viewIcon ${eyeIcon}"></i>
                    </div>
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Agreement Document">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    ${
                        row.active
                            ? ""
                            : `<div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Agreement Document">
                            <i class="deleteIcon ${deleteIcon}"></i>
                        </div>`
                    }
                </div>`;
    };

    const _openCreateEditAgreementDocumentModal = (document, { viewMode = false } = {}) => {
        createEditAgreementDocumentModalOpen = true;
        isViewAgreementDocumentModal = viewMode;
        const doc = document ?? {};
        const {
            documentType: docDocumentType,
            type: legacyType,
            ...rest
        } = doc;

        selectedAgreementDocument = {
            ...rest,
            documentType:
                docDocumentType !== undefined && docDocumentType !== null
                    ? docDocumentType.toString()
                    : legacyType !== undefined && legacyType !== null
                        ? legacyType.toString()
                        : "",
            name: doc.name ?? "",
            versionNumber: doc.versionNumber ?? "",
            content: doc.content ?? "",
            active:
                doc.active !== undefined && doc.active !== null
                    ? Boolean(doc.active)
                    : false,
        };
    };

    const _openDeleteAgreementDocumentModal = (document) => {
        deleteAgreementDocumentModalOpen = true;

        selectedAgreementDocument = document;
    };

    const _closeDeleteAgreementDocumentModal = () => {
        deleteAgreementDocumentModalOpen = false;

        selectedAgreementDocument = null;
    };

    const getAgreementDocumentTypeLabel = (documentTypeValue) => {
        const option = documentTypeOptions.find(
            (item) => item.value === documentTypeValue?.toString(),
        );

        return option ? option.label : "";
    };

    const _getDocumentIdFromURL = () => {
        if(!params?.query) return null;

        const arr = decodeSearchParams(params.query).split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("id="))
                    return Number(arr[i].split("=")[1]);
            }
        }

    };

    const _openAgreementDocumentById = async (id) => {
        if (!id) return;

        const documentInList = agreementDocuments.find((doc) => doc.id === id);

        if (documentInList) {
            _openCreateEditAgreementDocumentModal(documentInList, {
                viewMode: true,
            });
            _removeDocumentIdQueryParam();
            return;
        }

        try {
            const response = await apiMethods.getMethod.getAgreementDocuments(
                0,
                1,
                { id },
            );

            const fetchedDocument = response?.results?.[0];

            if (fetchedDocument) {
                _openCreateEditAgreementDocumentModal(fetchedDocument, {
                    viewMode: true,
                });
                _removeDocumentIdQueryParam();
            } else showError("Agreement document not found.");
        } catch (error) {
            showException(error);
        }
    };

    const _removeDocumentIdQueryParam = () => {
        if (typeof window === "undefined") return;

        const url = new URL(window.location.href);

        if (!url.searchParams.has("id")) return;

        url.searchParams.delete("id");

        const newPath = `${url.pathname}${url.search}${url.hash}`;

        replaceURL(newPath);
    };

    $: if (!createEditAgreementDocumentModalOpen) {
        isViewAgreementDocumentModal = false;
    }

</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 {$isMobile
            ? 'flex-col gap-y-4 items-start'
            : 'justify-between items-end'}"
    >
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />
        </div>

        <div class="flex justify-end {$isMobile ? '' : 'max-w-[50%]'}">
            <Button clickFunc={() => _openCreateEditAgreementDocumentModal({})}>
                Create Agreement Document
            </Button>
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAgreementDocuments}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={agreementDocuments}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={agreementDocuments}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: agreementDocuments,
            fileName: "AgreementDocuments",
            allDataApi: async () =>
                await apiMethods.getMethod.getAgreementDocuments(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if createEditAgreementDocumentModalOpen}
    <AddEditCompanyDocument 
        reload={reload}
        bind:selectedAgreementDocument={selectedAgreementDocument} 
        bind:createEditAgreementDocumentModalOpen={createEditAgreementDocumentModalOpen}
        isViewMode={isViewAgreementDocumentModal}>
    </AddEditCompanyDocument>
{/if}

<NotificationModal
    title="Delete Agreement Document"
    submitText={isDeletingAgreementDocument ? "Deleting" : "Delete"}
    confirmButtonClass="btn-error"
    cancelButtonClass="btn-accent"
    isModalOpen={deleteAgreementDocumentModalOpen}
    isConfirming={isDeletingAgreementDocument}
    closeModal={_closeDeleteAgreementDocumentModal}
    closeModalFunction={_closeDeleteAgreementDocumentModal}
    confirmAction={deleteAgreementDocument}
>
    Are you sure you want to delete the agreement document: {selectedAgreementDocument?.name}?
</NotificationModal>
